import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Home.css";

function Home() {
  const navigate = useNavigate();
  const [animatedStats, setAnimatedStats] = useState({ algorithms: 0, users: 0, datasets: 0 });

  useEffect(() => {
    // Animate stats on mount
    const animateValue = (start, end, duration, callback) => {
      let startTimestamp = null;
      const step = (timestamp) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        callback(Math.floor(progress * (end - start) + start));
        if (progress < 1) {
          window.requestAnimationFrame(step);
        }
      };
      window.requestAnimationFrame(step);
    };

    animateValue(0, 10, 2000, (val) => setAnimatedStats(prev => ({ ...prev, algorithms: val })));
    animateValue(0, 1000, 2000, (val) => setAnimatedStats(prev => ({ ...prev, users: val })));
    animateValue(0, 5000, 2000, (val) => setAnimatedStats(prev => ({ ...prev, datasets: val })));
  }, []);

  return (
    <div className="home-page">
      {/* Top Navigation Bar */}
      <div className="top-nav">
        <div className="nav-content">
          <div className="nav-logo-container">
            <img src="/HDASLogo.png" alt="HDAS" className="nav-logo" style={{ height: "80px", width: "auto" }} />
          </div>
          <div className="nav-buttons">
            <button className="nav-button sign-in" onClick={() => navigate("/login")}>
              Sign In
            </button>
            <button className="nav-button get-started" onClick={() => navigate("/register")}>
              Get Started
            </button>
          </div>
        </div>
      </div>

      {/* Header Section */}
      <div className="home-header">
        <div className="tech-background">
          <div className="floating-particles">
            {[...Array(20)].map((_, i) => (
              <div key={i} className="particle" style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 4}s`
              }}></div>
            ))}
          </div>
          <div className="circuit-lines"></div>
        </div>
        <div className="header-content">
          <h1 className="main-title">
            <span className="healthcare-text">Healthcare Data Analysis</span>
            <span className="highlight"> Made Simple</span>
          </h1>
          <p className="main-subtitle">
            Transform your healthcare data into actionable insights with zero coding required
          </p>
          <div className="header-description">
            <p>
              An automated machine learning platform designed specifically for healthcare professionals and students. 
              Whether you're analyzing patient records, clinical trial data, research datasets, or epidemiological studies, 
              our platform empowers you to perform sophisticated data analysis, predictive modeling, and generate publication-ready 
              visualizations - all through an intuitive, no-code interface.
            </p>
          </div>
          <div className="tech-stats">
            <div className="stat-item">
              <div className="stat-number">{animatedStats.algorithms}+</div>
              <div className="stat-label">ML Algorithms</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{animatedStats.users.toLocaleString()}+</div>
              <div className="stat-label">Active Users</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{animatedStats.datasets.toLocaleString()}+</div>
              <div className="stat-label">Datasets Analyzed</div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="features-section">
        <h2 className="section-title">Powerful Features</h2>
        <div className="features-grid">
          <div className="feature-card interactive-card">
            <div className="feature-icon-wrapper">
              <div className="feature-icon icon-upload"></div>
              <div className="icon-glow"></div>
            </div>
            <h3>Easy Data Upload</h3>
            <p>Simply upload your CSV files and let our platform handle the rest. No technical knowledge needed.</p>
            <div className="card-hover-effect"></div>
          </div>
          
          <div className="feature-card interactive-card">
            <div className="feature-icon-wrapper">
              <div className="feature-icon icon-analysis"></div>
              <div className="icon-glow"></div>
            </div>
            <h3>Advanced Analysis</h3>
            <p>Perform sophisticated statistical analysis and machine learning predictions with just a few clicks.</p>
            <div className="card-hover-effect"></div>
          </div>
          
          <div className="feature-card interactive-card">
            <div className="feature-icon-wrapper">
              <div className="feature-icon icon-reports"></div>
              <div className="icon-glow"></div>
            </div>
            <h3>Visual Reports</h3>
            <p>Generate beautiful graphs and visualizations perfect for your research papers and presentations.</p>
            <div className="card-hover-effect"></div>
          </div>
          
          <div className="feature-card interactive-card">
            <div className="feature-icon-wrapper">
              <div className="feature-icon icon-export"></div>
              <div className="icon-glow"></div>
            </div>
            <h3>Export Ready</h3>
            <p>Download your results and visualizations in formats ready for academic reports and publications.</p>
            <div className="card-hover-effect"></div>
          </div>
        </div>
      </div>

      {/* About Section */}
      <div className="about-section">
        <h2 className="section-title">What We Offer</h2>
        <div className="about-content">
          <p className="about-description">
            A comprehensive automated machine learning (AutoML) platform that brings the power of advanced data science 
            to healthcare professionals without requiring any programming knowledge. Built with healthcare data analysis in mind, 
            our platform streamlines the entire workflow from data cleaning to predictive modeling.
          </p>
          <div className="about-features">
            <div className="about-feature">
              <h3>Intelligent Data Processing</h3>
              <p>
                Automatically detects and handles missing values, suggests optimal cleaning strategies, and prepares your 
                dataset for analysis. Our smart algorithms identify data types, outliers, and inconsistencies to ensure high-quality results.
              </p>
            </div>
            <div className="about-feature">
              <h3>Advanced Machine Learning</h3>
              <p>
                Access state-of-the-art machine learning algorithms including classification and regression models. Our platform automatically 
                selects and tunes the best algorithms for your specific dataset, providing you with accurate predictions and insights.
              </p>
            </div>
            <div className="about-feature">
              <h3>Comprehensive Analytics</h3>
              <p>
                Generate detailed exploratory data analysis, correlation matrices, distribution plots, and predictive visualizations. 
                All visualizations are publication-ready and can be exported for your research papers and presentations.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="benefits-section">
        <h2 className="section-title">Perfect for Healthcare Professionals & Students</h2>
        <div className="benefits-list">
          <div className="benefit-item">
            <span className="checkmark">&#10003;</span>
            <span>Analyze patient data, clinical trials, research datasets, epidemiological studies, and more</span>
          </div>
          <div className="benefit-item">
            <span className="checkmark">&#10003;</span>
            <span>No programming skills required - focus on your research and analysis, not coding</span>
          </div>
          <div className="benefit-item">
            <span className="checkmark">&#10003;</span>
            <span>Professional visualizations and reports ready for academic papers and presentations</span>
          </div>
          <div className="benefit-item">
            <span className="checkmark">&#10003;</span>
            <span>Secure and private - your sensitive healthcare data stays protected and confidential</span>
          </div>
          <div className="benefit-item">
            <span className="checkmark">&#10003;</span>
            <span>Automated model selection and hyperparameter tuning for optimal results</span>
          </div>
          <div className="benefit-item">
            <span className="checkmark">&#10003;</span>
            <span>Export predictions and results in CSV format for further analysis</span>
          </div>
        </div>
      </div>

      {/* Use Cases Section */}
      <div className="use-cases-section">
        <h2 className="section-title">Who Can Use This Platform?</h2>
        <div className="use-cases-grid">
          <div className="use-case-card">
            <h3>Medical Students</h3>
            <p>
              Analyze research data for your thesis, dissertation, or academic projects. Generate publication-quality 
              visualizations and statistical reports without learning complex programming languages.
            </p>
          </div>
          <div className="use-case-card">
            <h3>Healthcare Researchers</h3>
            <p>
              Conduct predictive analysis on clinical trial data, patient outcomes, and treatment effectiveness. 
              Build machine learning models to identify patterns and make data-driven decisions.
            </p>
          </div>
          <div className="use-case-card">
            <h3>Public Health Professionals</h3>
            <p>
              Analyze epidemiological data, disease spread patterns, and population health metrics. 
              Create visualizations for reports and presentations to stakeholders.
            </p>
          </div>
          <div className="use-case-card">
            <h3>Healthcare Administrators</h3>
            <p>
              Gain insights from hospital data, patient satisfaction surveys, and operational metrics. 
              Make informed decisions based on comprehensive data analysis and predictive modeling.
            </p>
          </div>
        </div>
      </div>

      {/* Call-to-Action Section */}
      <div className="cta-section">
        <h2 className="cta-title">Ready to Transform Your Healthcare Data?</h2>
        <p className="cta-note">
          Join healthcare professionals and students who are already using our platform to unlock insights from their data. 
          Start analyzing your healthcare data in minutes - no coding required.
        </p>
      </div>
    </div>
  );
}

export default Home;
